import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { PgSelectComponent } from '../../pg-ui-controls/pg-select/pg-select.component';
import { AuthService } from '../../services/auth.service';
import { LocalizationService } from '../../services/localization.service';
import { PgProfileEditorService } from '../pg-profile-editor.service';
import { PgUserStatusService, WSServiceUserStatusValue } from '../pg-user-status.service';
import { NgbDropdown, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EnvironmentService } from '../../services/environment.service';
import { RoleData, UserDataRealm } from '../../services/data.service';
import { PgManageNotificationsComponent } from '../pg-manage-notifications/pg-manage-notifications.component';

export class PgUserMenuElementData {
    label:string;
    action?:() => void
}

export class PgUserMenuData {
    category?:string;
    elements:Array<PgUserMenuElementData>
}

@Component({
    selector: 'app-pg-user-menu',
    templateUrl: './pg-user-menu.component.html',
    styleUrls: ['./pg-user-menu.component.scss'],
    standalone: false
})
export class PgUserMenuComponent implements OnInit {

    constructor(private authService:AuthService, private pgProfileEditorService:PgProfileEditorService, private pgUserStatusService:PgUserStatusService, private localizationService:LocalizationService, private modalService:NgbModal, private environmentService:EnvironmentService) { }

    @Input() menuElements:Array<PgUserMenuData>
    @Input() requiredRealms:boolean

    @ViewChild(NgbDropdown) dropdownComponent:NgbDropdown;
    @Output() openChange = new EventEmitter<boolean>()

    registered = false;

    statusOptions = [
        { value: 'online', text: '' },
        { value: 'busy', text: '' },
        { value: 'offline', text: '' },
    ]

    userStatus:WSServiceUserStatusValue = null;

    languageOptions:Array<{ value: string, text: string }> = null;

    stripeAvailable = false;

    hasRealms = false;
    hasForceRealm = false;

    availableRoles:Array<RoleData> = null;

    ngOnInit(): void {
        this.stripeAvailable = this.environmentService.environment.StripeAPIKey != null && this.environmentService.environment.StripeAPIKey != '';

        this.registered = this.pgUserStatusService.registered;
        this.pgUserStatusService.registeredChange.subscribe(() => {
            this.registered = this.pgUserStatusService.registered;
        })
    
        this.userStatus = this.pgUserStatusService.userStatus;
        this.pgUserStatusService.userStatusChange.subscribe(() => {
            this.userStatus = this.pgUserStatusService.userStatus;
        })

        this._generateStatusOptionsLabels()


        this.languageOptions = [];

        for(let _language of this.localizationService.availableApplicationLanguages) {
            this.languageOptions.push({ value: _language, text: this.localizationService.languageLabels[_language] })
        }

        this.hasRealms = this.environmentService.hasRealms()
        this.hasForceRealm = this.environmentService.environment.ForceRealm != null;

        setTimeout(() => {
            this._checkRealmsWarning();
            this.authService.statusChange.subscribe(() => {
                this._checkRealmsWarning();
            })
        }, 2500)

        this._checkUserRoles()
        this.authService.statusChange.subscribe(() => {
            this._checkUserRoles();
        })
    }

    private _checkUserRoles() {
        this.availableRoles = [];

        if(this.authService.isLoggedIn()) {
            if(this.authService.isSuperAdmin() && !this.authService.accessMode) {
                this.availableRoles = this.authService.user.availableRoles;
            }
            else {
                for(let _role of this.authService.user.availableRoles) {
                    if(_role.name != 'base' && (this.authService.accessMode == null || _role.name == this.authService.accessMode)) {
                        this.availableRoles.push(_role)
                    }
                }
            }
        }
    }

    showRealmsWarning = null;

    private _checkRealmsWarning() {
        this.showRealmsWarning = false;
        
        if(this.isLoggedIn() && this.authService.accessMode == 'business' && !this.authService.isBusinessProfileIncomplete() && this.hasRealms && !this.hasUserSelectableRealms()) {
            this.showRealmsWarning = true;
        }
    }

    hasUserSelectableRealms() {
        if(this.isLoggedIn()) {
            for(let _realm of this.authService.user.getSelectableRealms()) {
                if(_realm.id != null) return true
            }
        }

        return false
    }

    @ViewChild('statusSelect') statusSelect:PgSelectComponent;

    private _generateStatusOptionsLabels() {
        for(let _option of this.statusOptions) {
            _option.text = '<i class="fa fa-circle ' + this.getStatusColorClass(_option.value as WSServiceUserStatusValue) + '"></i> ' + this.localizationService.translate('pg-user-menu.status-' + _option.value)
        }

        if(this.statusSelect != null) this.statusSelect.setText();
    }

    isLoggedIn() {
        return this.authService.isLoggedIn(true);
    }

    getUserName() {
        if(this.authService.user != null) {
            return this.authService.user.name + (this.authService.user.surname ? ' ' + this.authService.user.surname : '');   
        }
    }

    getUser() {
        return this.authService.user
    }

    getProfile() {
        return  this.authService.profile
    }

    isSwitchingLanguage = false;

    getLanguage() {
        return this.localizationService.currentLanguage;
    }

    setLanguage(val:string) {
        this.isSwitchingLanguage = true;
        this.localizationService.setLanguage(val).then(() => {
            this.isSwitchingLanguage = false;
        }, () => {
            this.isSwitchingLanguage = false;
        })
    }

    isSwitchingRealm = false;

    getUserRealm() {
        return this.authService.user.realm;
    }

    getUserRealmId() {
        let _realm = this.getUserRealm();
        if(_realm != null) return _realm.id
    }

    getAvailableRealms() {
        return this.authService.user.availableRealms;
    }

    getUnavailableRealms() {
        return this.authService.user.unavailableRealms;
    }

    getPendingRealms() {
        return this.authService.user.pendingRealms;
    }

    sortRealms(list:Array<UserDataRealm>) {
        let _result:Array<UserDataRealm> = []

        for(let _item of list) {
            _result.push(_item)
        }

        _result.sort((a, b) => {
            if(a.id == null && b.id != null) return -1
            else if(a.id != null && b.id == null) return 1
            else if(a.name.toLowerCase() < b.name.toLowerCase()) return -1
            else if(a.name.toLowerCase() > b.name.toLowerCase()) return 1
            else return 0
        })

        return _result
    }

    setUserRealm(id:string) {
        if(id != this.getUserRealmId()) {
            this.isSwitchingRealm = true;
            this.authService.setUserRealm(id).then(() => {
                this.isSwitchingRealm = false;
            }, () => {
                this.isSwitchingRealm = false;
            })
        }
    }

    getForceRealm() {
        return this.environmentService.getForceRealm()
    }

    getForceRealmStatus() {
        let _forcedRealm = this.getForceRealm()

        for(let _realm of this.getAvailableRealms()) {
            if(_realm.id == _forcedRealm.id) {
                return 'available'
            }
        }

        for(let _realm of this.getPendingRealms()) {
            if(_realm.id == _forcedRealm.id) {
                return 'pending'
            }
        }
    }

    @ViewChild('modalAddRealm') modalAddRealm:ElementRef;

    private _modalAddRealmRef:NgbModalRef = null;

    isRequestingAddRealm = false;

    editAddRealmValue:string = null;
    editAddRealmOptions:Array<{ value: string, text: string }> = null;

    openAddRealm() {
        this.editAddRealmValue = null;
        this.editAddRealmOptions = [];

        for(let _realm of this.environmentService.realmList) {
            let _isIn = false;

            for(let _item of this.authService.user.availableRealms) {
                if(_item.id == _realm.id) _isIn = true;
            }

            for(let _item of this.authService.user.unavailableRealms) {
                if(_item.id == _realm.id) _isIn = true;
            }

            for(let _item of this.authService.user.pendingRealms) {
                if(_item.id == _realm.id) _isIn = true;
            }

            if(!_isIn && !_realm.configuration.hideRequestAccess) {
                this.editAddRealmOptions.push({ value: _realm.id, text: _realm.name })
            }
        }

        this._modalAddRealmRef = this.modalService.open(this.modalAddRealm)
    }

    cancelAddRealm() {
        this._modalAddRealmRef.dismiss()
    }

    requestAddRealm() {
        if(!this.isRequestingAddRealm && this.editAddRealmValue) {
            this.isRequestingAddRealm = true;
    
            this.authService.requestAddRealm(this.editAddRealmValue).then((data) => {
                this.isRequestingAddRealm = false;
                this._modalAddRealmRef.dismiss()
            })
        }
    }

    getUserRole() {
        return this.authService.user.role;
    }

    getUserRoleId() {
        let _role = this.getUserRole();
        if(_role != null) return _role.id
    }

    setUserRole(id:string) {
        if(id != this.getUserRoleId()) {
            this.authService.setUserRole(id);
        }
    }

    getUserStripeId() {
        return this.authService.user.stripeId;
    }

    changePassword() {
        this.pgProfileEditorService.requestChangePassword();
    }

    doLogout() {
        this.authService.logout().then(() => {
        })
    }

    onMenuElementClick(element:PgUserMenuElementData) {
        if(element.action != null) {
            element.action();
        }
    }

    getUserGroups() {
        return this.authService.user.groups;
    }

    getStatusColorClass(status:WSServiceUserStatusValue) {
        switch(status) {
            case 'online': return 'text-success';
            case 'offline': return 'text-muted';
            case 'busy': return 'text-danger';
        }
    }

    setUserStatus(status:WSServiceUserStatusValue) {
        this.pgUserStatusService.setUserStatus(status)
    }

    requestEditProfile() {
        this.pgProfileEditorService.requestEditProfile()
    }

    isSuperAdmin() {
        return this.authService.isSuperAdmin()
    }

    disableTranslationToggle() {
        this.localizationService.setDisableTranslations(!this.localizationService.disableTranslations);

    }

    isTranslationDisabled() {
        return this.localizationService.disableTranslations;
    }

    openManageNotifications() {
        this.modalService.open(PgManageNotificationsComponent, { size: 'xl' });
    }
}
